import { useState, useRef } from 'react';
import { X } from 'lucide-react';
import type { SkillTrigger } from '@netsuite-ai/types';

interface Props {
  value: SkillTrigger[];
  onChange: (v: SkillTrigger[]) => void;
}

export default function Triggers({ value, onChange }: Props) {
  const keywords = value
    .filter((t) => t.type === 'keyword')
    .map((t) => t.value);
  const contexts = value
    .filter((t) => t.type === 'context')
    .map((t) => t.value);

  function setKeywords(vals: string[]) {
    const rest = value.filter((t) => t.type !== 'keyword');
    onChange([...rest, ...vals.map((v) => ({ type: 'keyword' as const, value: v }))]);
  }

  function setContexts(vals: string[]) {
    const rest = value.filter((t) => t.type !== 'context');
    onChange([...rest, ...vals.map((v) => ({ type: 'context' as const, value: v }))]);
  }

  return (
    <div className="space-y-4">
      <Field label="Trigger Keywords">
        <TagInput tags={keywords} onChange={setKeywords} />
      </Field>
      <Field label="Context Tags">
        <TagInput tags={contexts} onChange={setContexts} />
      </Field>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-neutral-600">{label}</p>
      {children}
    </div>
  );
}

function TagInput({
  tags,
  onChange,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
}) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function addTag(v: string) {
    const trimmed = v.trim().replace(/,$/, '');
    if (trimmed && !tags.includes(trimmed)) onChange([...tags, trimmed]);
    setInput('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input) {
      onChange(tags.slice(0, -1));
    }
  }

  return (
    <div
      className="flex cursor-text flex-wrap gap-1.5 rounded border border-neutral-300 px-3 py-2 focus-within:border-neutral-500"
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag, i) => (
        <span
          key={i}
          className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
        >
          {tag}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChange(tags.filter((_, j) => j !== i));
            }}
            aria-label={`Remove ${tag}`}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => addTag(input)}
        className="min-w-20 flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400"
        placeholder={tags.length === 0 ? 'Type and press Enter' : ''}
      />
    </div>
  );
}
